import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import Component from "./LinkCard";
import { getUrlMetaData } from "controller/api/general";

export default Node.create({
  name: "linkCard",

  group: "block",

  atom: true,

  addAttributes() {
    return {
      url: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-url"),
        renderHTML: (attributes) => {
          if (!attributes.url) return {};
          return { "data-url": attributes.url };
        },
      },
      title: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-title"),
        renderHTML: (attributes) => {
          if (!attributes.title) return {};
          return { "data-title": attributes.title };
        },
      },
      description: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-description"),
        renderHTML: (attributes) => {
          if (!attributes.description) return {};
          return { "data-description": attributes.description };
        },
      },
      src: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-src"),
        renderHTML: (attributes) => {
          if (!attributes.src) return {};
          return { "data-src": attributes.src };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-type='link-card']",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "link-card" }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },

  addPasteRules() {
    const fetchUrlMetadata = async (url: string) => {
      try {
        const domain = url.replace(/^https?:\/\//, "").split("/")[0];
        const pathname = new URL(url).pathname;
        const title =
          pathname === "/" ? domain : pathname.split("/").pop() || domain;

        const metadata = await getUrlMetaData(url);
        console.log(metadata);
        if (metadata) {
          return {
            title: metadata.title,
            description: metadata.description,
            src:
              metadata.s3_url ||
              `https://www.google.com/s2/favicons?domain=${domain}&sz=64` ||
              metadata.url,
            isLoading: false,
          };
        }
        return {
          title: title.charAt(0).toUpperCase() + title.slice(1),
          description: `Visit ${domain}`,
          src: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
          isLoading: false,
        };
      } catch (error) {
        console.error("Error fetching URL metadata:", error);
        const domain = url.replace(/^https?:\/\//, "").split("/")[0];
        return {
          title: domain,
          description: "Unable to load preview",
          src: "",
          isLoading: false,
        };
      }
    };

    return [
      {
        find: /^https?:\/\/[^\s$.?#].[^\s]*$/gi,
        handler: ({ range, match, commands, state }) => {
          const url = match[0];
          const domain = url.replace(/^https?:\/\//, "").split("/")[0];

          const { $from } = state.selection;
          const currentNode = $from.parent;
          console.log(currentNode?.textContent);
          const isEmpty =
            !currentNode.textContent || currentNode.textContent.trim() === url;
          if (isEmpty) {
            // Insert initial link card with basic info
            commands.insertContentAt(range, {
              type: this.name,
              attrs: {
                url,
                title: domain,
                description: "Loading...",
                src: "",
                isLoading: true,
              },
            });

            // Fetch metadata asynchronously and update
            fetchUrlMetadata(url).then((metadata) => {
              const editor = this.editor;
              if (editor && editor.view) {
                // Find the inserted node and update it
                const { state } = editor.view;
                const { tr } = state;
                let linkCardPos = -1;

                state.doc.descendants((node, pos) => {
                  if (node.type.name === this.name && node.attrs.url === url) {
                    linkCardPos = pos;
                    return false;
                  }
                });

                if (linkCardPos !== -1) {
                  tr.setNodeMarkup(linkCardPos, undefined, {
                    url,
                    ...metadata,
                  });
                  editor.view.dispatch(tr);
                }
              }
            });
          }
        },
      },
    ];
  },
});
